import { useState, useEffect, useRef } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase-config';
import { Input } from './ui/input';

interface User {
  id: string;
  email: string;
  name?: string;
  customerId?: string;
  createdAt: Date;
  createdVia?: 'self' | 'admin_proxy';
  pendingVerification?: boolean;
}

interface UserSearchAutocompleteProps {
  customerId: string;
  onUserSelect: (user: User) => void;
  onCreateNew: (email: string) => void;
  placeholder?: string;
  className?: string;
}

export function UserSearchAutocomplete({
  customerId,
  onUserSelect,
  onCreateNew,
  placeholder = 'Search by email...',
  className = '',
}: UserSearchAutocompleteProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Debounced search
  useEffect(() => {
    if (!searchTerm.trim()) {
      setUsers([]);
      setIsOpen(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsLoading(true);
      try {
        const usersRef = collection(db, 'users');

        // Try to get users for this customer first
        let allUsers: User[] = [];

        try {
          const qWithCustomer = query(
            usersRef,
            where('customerId', '==', customerId)
          );
          const snapshotWithCustomer = await getDocs(qWithCustomer);
          allUsers = snapshotWithCustomer.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate(),
          })) as User[];

          console.log(`Found ${allUsers.length} users with customerId ${customerId}`);
        } catch (err) {
          console.log('Error querying with customerId, trying without filter:', err);
        }

        // If no users found with customerId, try getting all users and filter by email
        if (allUsers.length === 0) {
          console.log('No users found with customerId filter, searching all users...');
          const snapshotAll = await getDocs(usersRef);
          allUsers = snapshotAll.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate(),
          })) as User[];
          console.log(`Found ${allUsers.length} total users in database`);
        }

        // Filter by email client-side (case-insensitive, partial match)
        const searchLower = searchTerm.toLowerCase();
        const filteredUsers = allUsers
          .filter(user => user.email && user.email.toLowerCase().includes(searchLower))
          .sort((a, b) => a.email.localeCompare(b.email))
          .slice(0, 10);

        console.log(`Filtered to ${filteredUsers.length} users matching "${searchTerm}"`);
        setUsers(filteredUsers);
        setIsOpen(true);
      } catch (error) {
        console.error('Error searching users:', error);
        setUsers([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, customerId]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < users.length ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > -1 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex === users.length) {
          // Create new user option
          onCreateNew(searchTerm);
          setIsOpen(false);
          setSearchTerm('');
        } else if (selectedIndex >= 0 && selectedIndex < users.length) {
          onUserSelect(users[selectedIndex]);
          setIsOpen(false);
          setSearchTerm('');
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleUserClick = (user: User) => {
    onUserSelect(user);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleCreateNewClick = () => {
    onCreateNew(searchTerm);
    setIsOpen(false);
    setSearchTerm('');
  };

  const exactMatch = users.some(
    u => u.email.toLowerCase() === searchTerm.toLowerCase()
  );

  const showCreateOption = searchTerm.trim() && !exactMatch;

  return (
    <div className={`relative ${className}`}>
      <Input
        ref={inputRef}
        type="email"
        placeholder={placeholder}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => searchTerm.trim() && setIsOpen(true)}
        className="w-full"
        aria-label="Search for user by email"
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-controls="user-search-dropdown"
      />

      {isOpen && (searchTerm.trim() || isLoading) && (
        <div
          ref={dropdownRef}
          id="user-search-dropdown"
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-80 overflow-auto"
          role="listbox"
        >
          {isLoading && (
            <div className="px-4 py-3 text-sm text-gray-500">
              Searching...
            </div>
          )}

          {!isLoading && users.length === 0 && !showCreateOption && (
            <div className="px-4 py-3 text-sm text-gray-500">
              No users found
            </div>
          )}

          {!isLoading && users.length > 0 && (
            <div className="py-1">
              {users.map((user, index) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => handleUserClick(user)}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center gap-2 ${
                    selectedIndex === index ? 'bg-gray-100' : ''
                  }`}
                  role="option"
                  aria-selected={selectedIndex === index}
                >
                  <svg
                    className="w-4 h-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">
                      {user.email}
                    </div>
                    {user.name && (
                      <div className="text-xs text-gray-500">{user.name}</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {!isLoading && showCreateOption && (
            <>
              {users.length > 0 && (
                <div className="border-t border-gray-200 my-1"></div>
              )}
              <button
                type="button"
                onClick={handleCreateNewClick}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center gap-2 ${
                  selectedIndex === users.length ? 'bg-gray-100' : ''
                }`}
                role="option"
                aria-selected={selectedIndex === users.length}
              >
                <svg
                  className="w-4 h-4 text-blue-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                <div className="flex-1">
                  <div className="font-medium text-blue-600">
                    Create new user
                  </div>
                  <div className="text-xs text-gray-500">{searchTerm}</div>
                </div>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
